import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Calendar as CalendarIcon, Trash2, LogOut, Lock, Phone, Mail, User, Clock, 
  Scissors, ChevronLeft, ChevronRight, DollarSign, Users, TrendingUp, 
  AlertTriangle, Bell, Plus, Edit2, Send, Search, BarChart3, UserX, 
  CalendarDays, MessageSquare, CalendarClock, Save, RefreshCw, Menu, X,
  ChevronDown, ChevronUp
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mobile-friendly booking card component
const BookingCard = ({ booking, formatTime, onEdit, onNoShow, onCancel }) => {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <Card className={cn(
      "border-zinc-800 bg-zinc-900 overflow-hidden transition-all",
      booking.no_show && "opacity-60"
    )}>
      <CardContent className="p-0">
        <div 
          className="p-4 flex items-center justify-between cursor-pointer"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-14 h-14 rounded-xl bg-zinc-800 flex flex-col items-center justify-center flex-shrink-0">
              <span className="text-lg font-bold text-white">{formatTime(booking.time).split(' ')[0]}</span>
              <span className="text-xs text-zinc-400">{formatTime(booking.time).split(' ')[1]}</span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-white truncate">{booking.customer_name}</p>
              <p className="text-sm text-zinc-400 truncate">{booking.service_name}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-emerald-500 font-medium">${booking.service_price || 0}</span>
                {booking.no_show ? (
                  <span className="px-2 py-0.5 bg-red-500/20 text-red-400 rounded text-xs">No-Show</span>
                ) : booking.status === "cancelled" ? (
                  <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded text-xs">Cancelled</span>
                ) : (
                  <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded text-xs">Confirmed</span>
                )}
              </div>
            </div>
          </div>
          {expanded ? <ChevronUp className="h-5 w-5 text-zinc-500" /> : <ChevronDown className="h-5 w-5 text-zinc-500" />}
        </div>
        
        {expanded && (
          <div className="px-4 pb-4 pt-0 border-t border-zinc-800 space-y-3">
            <div className="flex items-center gap-2 text-sm text-zinc-400 pt-3">
              <Phone className="h-4 w-4" />
              <a href={`tel:${booking.customer_phone}`} className="hover:text-white">{booking.customer_phone}</a>
            </div>
            <div className="flex items-center gap-2 text-sm text-zinc-400">
              <Mail className="h-4 w-4" />
              <a href={`mailto:${booking.customer_email}`} className="hover:text-white truncate">{booking.customer_email}</a>
            </div>
            {booking.notes && (
              <div className="text-sm text-zinc-400 bg-zinc-800/50 p-2 rounded">
                <span className="text-zinc-500">Notes:</span> {booking.notes}
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <Button size="sm" variant="outline" className="flex-1 border-zinc-700" onClick={() => onEdit(booking)}>
                <Edit2 className="h-4 w-4 mr-1" /> Notes
              </Button>
              {!booking.no_show && booking.status !== "cancelled" && (
                <Button size="sm" variant="outline" className="border-yellow-500/50 text-yellow-400" onClick={() => onNoShow(booking.id)}>
                  <UserX className="h-4 w-4" />
                </Button>
              )}
              {booking.status !== "cancelled" && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="outline" className="border-red-500/50 text-red-400">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-zinc-900 border-zinc-800 mx-4">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Cancel Booking?</AlertDialogTitle>
                      <AlertDialogDescription className="text-zinc-400">
                        Cancel {booking.customer_name}'s appointment at {formatTime(booking.time)}?
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="gap-2">
                      <AlertDialogCancel className="border-zinc-700">Keep</AlertDialogCancel>
                      <AlertDialogAction onClick={() => onCancel(booking.id)} className="bg-red-600">Cancel</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [storedPassword, setStoredPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [activeTab, setActiveTab] = useState("dashboard");
  
  // Dashboard state
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  
  // Bookings state
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [bookingNotes, setBookingNotes] = useState("");
  const [syncing, setSyncing] = useState(false);
  
  // Services state
  const [services, setServices] = useState([]);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceForm, setServiceForm] = useState({ name: "", price: "", duration: "", description: "" });
  
  // Barbers state
  const [barbers, setBarbers] = useState([]);
  const [showBarberModal, setShowBarberModal] = useState(false);
  const [editingBarber, setEditingBarber] = useState(null);
  const [barberForm, setBarberForm] = useState({ name: "", bio: "", image_url: "" });
  
  // Customers state
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState("");
  
  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [smsPhone, setSmsPhone] = useState("");
  const [smsMessage, setSmsMessage] = useState("");
  const [sendingSms, setSendingSms] = useState(false);
  
  // Schedule editor state
  const [schedule, setSchedule] = useState(null);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [editedAvailability, setEditedAvailability] = useState([]);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [overrideDate, setOverrideDate] = useState(null);
  const [overrideStartTime, setOverrideStartTime] = useState("10:00");
  const [overrideEndTime, setOverrideEndTime] = useState("18:00");
  const [overrideType, setOverrideType] = useState("custom");

  const DAYS_OF_WEEK = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const TIME_OPTIONS = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      TIME_OPTIONS.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
    }
  }

  const TABS = [
    { id: "dashboard", label: "Home", icon: BarChart3 },
    { id: "bookings", label: "Bookings", icon: CalendarDays },
    { id: "schedule", label: "Schedule", icon: CalendarClock },
    { id: "services", label: "Services", icon: Scissors },
    { id: "customers", label: "Clients", icon: Users },
    { id: "notifications", label: "Alerts", icon: Bell },
  ];

  // Fetch data when authenticated
  useEffect(() => {
    if (isAuthenticated && storedPassword) {
      if (activeTab === "dashboard") fetchStats();
      else if (activeTab === "bookings") fetchBookings();
      else if (activeTab === "services") fetchServices();
      else if (activeTab === "barbers") fetchBarbers();
      else if (activeTab === "customers") fetchCustomers();
      else if (activeTab === "notifications") fetchNotifications();
      else if (activeTab === "schedule") fetchSchedule();
    }
  }, [isAuthenticated, activeTab, selectedDate, storedPassword]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      await axios.post(`${API}/admin/login`, { password });
      setIsAuthenticated(true);
      setStoredPassword(password);
      toast.success("Welcome back!");
    } catch (error) {
      toast.error("Invalid password");
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setStoredPassword("");
    setPassword("");
  };

  const fetchStats = async () => {
    setLoadingStats(true);
    try {
      const response = await axios.get(`${API}/admin/dashboard?password=${storedPassword}`);
      setStats(response.data);
    } catch (error) {
      toast.error("Failed to load stats");
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchBookings = async () => {
    setLoadingBookings(true);
    try {
      const dateStr = selectedDate.toISOString().split("T")[0];
      const response = await axios.get(`${API}/admin/bookings?password=${storedPassword}&date=${dateStr}`);
      setBookings(response.data);
    } catch (error) {
      toast.error("Failed to load bookings");
    } finally {
      setLoadingBookings(false);
    }
  };

  const refreshBookings = async () => {
    setSyncing(true);
    try {
      await fetchBookings();
      toast.success("Bookings refreshed!");
    } catch (error) {
      toast.error("Refresh failed");
    } finally {
      setSyncing(false);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/admin/services?password=${storedPassword}`);
      setServices(response.data);
    } catch (error) {
      toast.error("Failed to load services");
    }
  };

  const fetchBarbers = async () => {
    try {
      const response = await axios.get(`${API}/admin/barbers?password=${storedPassword}`);
      setBarbers(response.data);
    } catch (error) {
      toast.error("Failed to load barbers");
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/admin/customers?password=${storedPassword}&search=${customerSearch}`);
      setCustomers(response.data);
    } catch (error) {
      toast.error("Failed to load customers");
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/admin/notifications?password=${storedPassword}&limit=50`);
      setNotifications(response.data);
    } catch (error) {
      toast.error("Failed to load notifications");
    }
  };

  const fetchSchedule = async () => {
    setLoadingSchedule(true);
    try {
      const response = await axios.get(`${API}/admin/schedule?password=${storedPassword}`);
      if (response.data.success && response.data.schedule) {
        setSchedule(response.data.schedule);
        // Map backend format {day, enabled, start_time, end_time} to frontend format {days: [day], startTime, endTime}
        const days = response.data.schedule.days || [];
        const mapped = days
          .filter(d => d.enabled)
          .map(d => ({ days: [d.day], startTime: d.start_time, endTime: d.end_time }));
        setEditedAvailability(mapped);
      } else {
        setSchedule(null);
        setEditedAvailability([]);
      }
    } catch (error) {
      toast.error("Failed to load schedule");
    } finally {
      setLoadingSchedule(false);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    try {
      await axios.delete(`${API}/admin/bookings/${bookingId}?password=${storedPassword}`);
      toast.success("Booking cancelled");
      fetchBookings();
      fetchStats();
    } catch (error) {
      toast.error("Failed to cancel booking");
    }
  };

  const handleMarkNoShow = async (bookingId) => {
    try {
      await axios.patch(`${API}/admin/bookings/${bookingId}?password=${storedPassword}`, { no_show: true });
      toast.success("Marked as no-show");
      fetchBookings();
      fetchStats();
    } catch (error) {
      toast.error("Failed to update booking");
    }
  };

  const handleSaveNotes = async (bookingId) => {
    try {
      await axios.patch(`${API}/admin/bookings/${bookingId}?password=${storedPassword}`, { notes: bookingNotes });
      toast.success("Notes saved");
      setSelectedBooking(null);
      fetchBookings();
    } catch (error) {
      toast.error("Failed to save notes");
    }
  };

  const handleSaveService = async () => {
    try {
      if (editingService) {
        await axios.patch(`${API}/admin/services/${editingService.id}?password=${storedPassword}`, {
          name: serviceForm.name,
          price: parseFloat(serviceForm.price),
          duration: parseInt(serviceForm.duration),
          description: serviceForm.description
        });
        toast.success("Service updated");
      } else {
        await axios.post(`${API}/admin/services?password=${storedPassword}`, {
          name: serviceForm.name,
          price: parseFloat(serviceForm.price),
          duration: parseInt(serviceForm.duration),
          description: serviceForm.description
        });
        toast.success("Service created");
      }
      setShowServiceModal(false);
      setEditingService(null);
      setServiceForm({ name: "", price: "", duration: "", description: "" });
      fetchServices();
    } catch (error) {
      toast.error("Failed to save service");
    }
  };

  const handleDeleteService = async (serviceId) => {
    try {
      await axios.delete(`${API}/admin/services/${serviceId}?password=${storedPassword}`);
      toast.success("Service deleted");
      fetchServices();
    } catch (error) {
      toast.error("Failed to delete service");
    }
  };

  const handleSaveSchedule = async () => {
    if (!schedule?.id) return;
    setSavingSchedule(true);
    try {
      // Convert frontend format back to backend format
      const allDays = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
      const days = allDays.map(dayName => {
        const avail = editedAvailability.find(a => a.days.includes(dayName));
        return {
          day: dayName,
          enabled: !!avail,
          start_time: avail ? avail.startTime : "10:00",
          end_time: avail ? avail.endTime : "20:00"
        };
      });
      const response = await axios.patch(
        `${API}/admin/schedule?password=${storedPassword}`,
        { days }
      );
      if (response.data.success) {
        setSchedule(response.data.schedule);
        toast.success("Schedule updated!");
      }
    } catch (error) {
      toast.error("Failed to save schedule");
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleAddOverride = async () => {
    if (!schedule?.id || !overrideDate) return;
    const dateStr = overrideDate.toISOString().split("T")[0];
    try {
      const response = await axios.post(
        `${API}/admin/schedule/override?password=${storedPassword}`,
        overrideType === "off" 
          ? { date: dateStr, enabled: false, start_time: "00:00", end_time: "00:00" }
          : { date: dateStr, enabled: true, start_time: overrideStartTime, end_time: overrideEndTime }
      );
      if (response.data.success) {
        setSchedule(response.data.schedule);
        toast.success(overrideType === "off" ? "Day off added" : "Override added");
      }
      setShowOverrideModal(false);
      setOverrideDate(null);
    } catch (error) {
      toast.error("Failed to add override");
    }
  };

  const handleRemoveOverride = async (date) => {
    if (!schedule?.id) return;
    try {
      const response = await axios.delete(
        `${API}/admin/schedule/override/${date}?password=${storedPassword}`
      );
      if (response.data.success) {
        setSchedule(response.data.schedule);
        toast.success("Override removed");
      }
    } catch (error) {
      toast.error("Failed to remove override");
    }
  };

  const toggleDayAvailability = (day) => {
    setEditedAvailability(prev => {
      const dayIndex = prev.findIndex(a => a.days.includes(day));
      if (dayIndex >= 0) {
        const updated = [...prev];
        updated[dayIndex] = { ...updated[dayIndex], days: updated[dayIndex].days.filter(d => d !== day) };
        return updated.filter(a => a.days.length > 0);
      } else {
        return [...prev, { days: [day], startTime: "10:00", endTime: "20:00" }];
      }
    });
  };

  const updateDayTimes = (day, field, value) => {
    setEditedAvailability(prev => prev.map(a => a.days.includes(day) ? { ...a, [field]: value } : a));
  };

  const getDayAvailability = (day) => editedAvailability.find(a => a.days.includes(day));

  const handleSendSms = async () => {
    if (!smsPhone || !smsMessage) {
      toast.error("Phone and message required");
      return;
    }
    setSendingSms(true);
    try {
      await axios.post(`${API}/admin/send-sms?password=${storedPassword}&phone=${smsPhone}&message=${encodeURIComponent(smsMessage)}`);
      toast.success("SMS sent!");
      setSmsPhone("");
      setSmsMessage("");
      fetchNotifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send SMS");
    } finally {
      setSendingSms(false);
    }
  };

  const formatTime = (time) => {
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatTimeDisplay = (time24) => {
    if (!time24) return "";
    const [hours, minutes] = time24.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const navigateDate = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + direction);
    setSelectedDate(newDate);
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4">
        <Card className="w-full max-w-sm border-zinc-800 bg-zinc-900">
          <CardHeader className="text-center pb-2">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-zinc-700 to-zinc-900 flex items-center justify-center mx-auto mb-4 border border-zinc-700">
              <Lock className="h-10 w-10 text-white" />
            </div>
            <CardTitle className="text-2xl tracking-wider" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              ADMIN ACCESS
            </CardTitle>
            <p className="text-zinc-500 text-sm">Showtime Shobi</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <Input
                type="password"
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-zinc-800 border-zinc-700 h-14 text-center text-lg"
                data-testid="admin-password-input"
              />
              <Button 
                type="submit" 
                className="w-full bg-white text-zinc-950 hover:bg-zinc-200 h-14 text-lg font-semibold" 
                disabled={loggingIn} 
                data-testid="admin-login-btn"
              >
                {loggingIn ? "Authenticating..." : "Login"}
              </Button>
            </form>
            <div className="mt-6 text-center">
              <a href="/" className="text-sm text-zinc-500 hover:text-zinc-300 flex items-center justify-center gap-1">
                <ChevronLeft className="h-4 w-4" /> Back to Booking
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-zinc-950 pb-20">
      {/* Header */}
      <header className="bg-zinc-900/95 backdrop-blur-sm border-b border-zinc-800 sticky top-0 z-50">
        <div className="px-4 py-3 flex items-center justify-between">
          <h1 className="text-lg font-bold tracking-wider" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            SHOWTIME ADMIN
          </h1>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-zinc-400" data-testid="admin-logout-btn">
            <LogOut className="h-5 w-5" />
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 py-4 max-w-2xl mx-auto">
        
        {/* Dashboard Tab */}
        {activeTab === "dashboard" && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold">Dashboard</h2>
            
            {loadingStats ? (
              <div className="text-center py-12 text-zinc-400">Loading...</div>
            ) : stats && (
              <>
                {/* Today's Stats - Hero Card */}
                <Card className="border-zinc-800 bg-gradient-to-br from-zinc-900 to-zinc-950 overflow-hidden">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-zinc-400 text-sm uppercase tracking-wider">Today</span>
                      <CalendarDays className="h-5 w-5 text-zinc-600" />
                    </div>
                    <div className="flex items-end justify-between">
                      <div>
                        <p className="text-5xl font-bold text-white">{stats.total_bookings_today}</p>
                        <p className="text-zinc-500 mt-1">appointments</p>
                      </div>
                      <div className="text-right">
                        <p className="text-3xl font-bold text-emerald-500">${stats.revenue_today}</p>
                        <p className="text-zinc-500">revenue</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-3">
                  <Card className="border-zinc-800 bg-zinc-900">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="h-4 w-4 text-blue-400" />
                        <span className="text-zinc-400 text-xs uppercase">This Week</span>
                      </div>
                      <p className="text-2xl font-bold text-white">{stats.total_bookings_week}</p>
                      <p className="text-emerald-500 text-sm">${stats.revenue_week}</p>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-zinc-800 bg-zinc-900">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <DollarSign className="h-4 w-4 text-emerald-400" />
                        <span className="text-zinc-400 text-xs uppercase">This Month</span>
                      </div>
                      <p className="text-2xl font-bold text-white">{stats.total_bookings_month}</p>
                      <p className="text-emerald-500 text-sm">${stats.revenue_month}</p>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-zinc-800 bg-zinc-900">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="h-4 w-4 text-amber-400" />
                        <span className="text-zinc-400 text-xs uppercase">Upcoming</span>
                      </div>
                      <p className="text-2xl font-bold text-white">{stats.upcoming_appointments}</p>
                      <p className="text-zinc-500 text-sm">scheduled</p>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-zinc-800 bg-zinc-900">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="h-4 w-4 text-red-400" />
                        <span className="text-zinc-400 text-xs uppercase">Issues</span>
                      </div>
                      <p className="text-2xl font-bold text-white">{stats.no_shows_month + stats.cancellations_month}</p>
                      <p className="text-zinc-500 text-sm">no-shows + cancels</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Quick Actions */}
                <div className="flex gap-2 mt-4">
                  <Button 
                    onClick={() => setActiveTab("bookings")} 
                    className="flex-1 bg-white text-zinc-950 hover:bg-zinc-200 h-12"
                  >
                    <CalendarDays className="h-4 w-4 mr-2" /> View Bookings
                  </Button>
                  <Button 
                    onClick={() => setActiveTab("schedule")} 
                    variant="outline" 
                    className="flex-1 border-zinc-700 h-12"
                  >
                    <CalendarClock className="h-4 w-4 mr-2" /> Schedule
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Bookings Tab */}
        {activeTab === "bookings" && (
          <div className="space-y-4">
            {/* Date Navigation */}
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="icon" onClick={() => navigateDate(-1)} className="h-10 w-10">
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <button 
                onClick={() => setShowCalendar(!showCalendar)} 
                className="flex-1 mx-2 py-3 px-4 bg-zinc-900 rounded-xl border border-zinc-800 text-center"
              >
                <p className="text-lg font-semibold text-white">
                  {selectedDate.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                </p>
                <p className="text-xs text-zinc-500">{bookings.length} booking{bookings.length !== 1 ? "s" : ""}</p>
              </button>
              <Button variant="ghost" size="icon" onClick={() => navigateDate(1)} className="h-10 w-10">
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>

            {/* Today Button + Sync */}
            <div className="flex justify-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setSelectedDate(new Date())} 
                className="border-zinc-700 text-xs"
              >
                Jump to Today
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={refreshBookings}
                disabled={syncing}
                className="border-zinc-700 text-xs"
              >
                <RefreshCw className={cn("h-3 w-3 mr-1", syncing && "animate-spin")} />
                {syncing ? "Refreshing..." : "Refresh"}
              </Button>
            </div>

            {showCalendar && (
              <Card className="border-zinc-800 bg-zinc-900">
                <CardContent className="p-2">
                  <Calendar 
                    mode="single" 
                    selected={selectedDate} 
                    onSelect={(date) => { if (date) { setSelectedDate(date); setShowCalendar(false); }}}
                    className="mx-auto"
                  />
                </CardContent>
              </Card>
            )}

            {/* Bookings List */}
            {loadingBookings ? (
              <div className="py-12 text-center text-zinc-400">Loading...</div>
            ) : bookings.length === 0 ? (
              <Card className="border-zinc-800 bg-zinc-900">
                <CardContent className="py-12 text-center">
                  <CalendarIcon className="h-12 w-12 text-zinc-700 mx-auto mb-4" />
                  <p className="text-zinc-400">No bookings for this date</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {bookings.sort((a, b) => a.time.localeCompare(b.time)).map((booking) => (
                  <BookingCard
                    key={booking.id}
                    booking={booking}
                    formatTime={formatTime}
                    onEdit={(b) => { setSelectedBooking(b); setBookingNotes(b.notes || ""); }}
                    onNoShow={handleMarkNoShow}
                    onCancel={handleCancelBooking}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Schedule Tab */}
        {activeTab === "schedule" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold">Work Schedule</h2>
                <p className="text-zinc-500 text-sm">Manage your availability</p>
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" size="icon" onClick={fetchSchedule} disabled={loadingSchedule}>
                  <RefreshCw className={cn("h-5 w-5", loadingSchedule && "animate-spin")} />
                </Button>
                <Button onClick={handleSaveSchedule} disabled={savingSchedule || !schedule} size="sm" className="bg-white text-zinc-950">
                  <Save className="h-4 w-4 mr-1" /> Save
                </Button>
              </div>
            </div>

            {loadingSchedule ? (
              <div className="py-12 text-center text-zinc-400">Loading schedule...</div>
            ) : !schedule ? (
              <Card className="border-zinc-800 bg-zinc-900">
                <CardContent className="py-12 text-center">
                  <CalendarClock className="h-12 w-12 text-zinc-700 mx-auto mb-4" />
                  <p className="text-zinc-400">No schedule found</p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Weekly Hours */}
                <Card className="border-zinc-800 bg-zinc-900">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Weekly Hours</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {DAYS_OF_WEEK.map((day) => {
                      const dayAvail = getDayAvailability(day);
                      const isActive = !!dayAvail;
                      
                      return (
                        <div 
                          key={day} 
                          className={cn(
                            "flex items-center gap-3 p-3 rounded-lg border transition-all",
                            isActive ? "border-emerald-500/30 bg-emerald-500/5" : "border-zinc-800 bg-zinc-800/30"
                          )}
                        >
                          <Switch
                            checked={isActive}
                            onCheckedChange={() => toggleDayAvailability(day)}
                            data-testid={`toggle-${day.toLowerCase()}`}
                          />
                          <span className={cn("font-medium w-12 text-sm", isActive ? "text-white" : "text-zinc-500")}>
                            {day.slice(0, 3)}
                          </span>
                          
                          {isActive ? (
                            <div className="flex items-center gap-1 flex-1 justify-end">
                              <Select value={dayAvail?.startTime || "10:00"} onValueChange={(v) => updateDayTimes(day, "startTime", v)}>
                                <SelectTrigger className="w-[90px] h-8 text-xs bg-zinc-800 border-zinc-700">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-zinc-900 border-zinc-800 max-h-[200px]">
                                  {TIME_OPTIONS.map((t) => (
                                    <SelectItem key={t} value={t} className="text-xs">{formatTimeDisplay(t)}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <span className="text-zinc-600 text-xs">-</span>
                              <Select value={dayAvail?.endTime || "20:00"} onValueChange={(v) => updateDayTimes(day, "endTime", v)}>
                                <SelectTrigger className="w-[90px] h-8 text-xs bg-zinc-800 border-zinc-700">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-zinc-900 border-zinc-800 max-h-[200px]">
                                  {TIME_OPTIONS.map((t) => (
                                    <SelectItem key={t} value={t} className="text-xs">{formatTimeDisplay(t)}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          ) : (
                            <span className="text-zinc-600 text-xs ml-auto">Off</span>
                          )}
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>

                {/* Date Overrides */}
                <Card className="border-zinc-800 bg-zinc-900">
                  <CardHeader className="pb-2 flex flex-row items-center justify-between">
                    <CardTitle className="text-base">Date Overrides</CardTitle>
                    <Button variant="outline" size="sm" onClick={() => setShowOverrideModal(true)} className="border-zinc-700 h-8">
                      <Plus className="h-4 w-4 mr-1" /> Add
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {schedule.overrides && schedule.overrides.length > 0 ? (
                      <div className="space-y-2">
                        {schedule.overrides.map((override, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <CalendarIcon className="h-4 w-4 text-zinc-500" />
                              <div>
                                <p className="font-medium text-sm">{override.date}</p>
                                {(!override.enabled || (override.start_time === "00:00" && override.end_time === "00:00")) ? (
                                  <p className="text-red-400 text-xs">Day Off</p>
                                ) : (
                                  <p className="text-zinc-400 text-xs">
                                    {formatTimeDisplay(override.start_time)} - {formatTimeDisplay(override.end_time)}
                                  </p>
                                )}
                              </div>
                            </div>
                            <Button variant="ghost" size="icon" onClick={() => handleRemoveOverride(override.date)} className="h-8 w-8 text-red-400">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-zinc-500 text-sm text-center py-4">No date overrides set</p>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        )}

        {/* Services Tab */}
        {activeTab === "services" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold">Services</h2>
              <Button onClick={() => { setEditingService(null); setServiceForm({ name: "", price: "", duration: "", description: "" }); setShowServiceModal(true); }} size="sm" className="bg-white text-zinc-950">
                <Plus className="h-4 w-4 mr-1" /> Add
              </Button>
            </div>
            
            <div className="space-y-3">
              {services.filter(s => s.active !== false).map((service) => (
                <Card key={service.id} className="border-zinc-800 bg-zinc-900">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-white">{service.name}</h3>
                          <span className="text-xs text-zinc-500 bg-zinc-800 px-2 py-0.5 rounded">{service.duration}min</span>
                        </div>
                        <p className="text-sm text-zinc-400 mt-1">{service.description}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xl font-bold text-emerald-500">${service.price}</span>
                        <Button variant="ghost" size="icon" onClick={() => { setEditingService(service); setServiceForm({ name: service.name, price: service.price.toString(), duration: service.duration.toString(), description: service.description }); setShowServiceModal(true); }} className="h-8 w-8">
                          <Edit2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Customers Tab */}
        {activeTab === "customers" && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold">Clients</h2>
            
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
              <Input
                placeholder="Search clients..."
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && fetchCustomers()}
                className="pl-10 bg-zinc-900 border-zinc-800 h-12"
              />
            </div>
            
            <div className="space-y-2">
              {customers.map((customer, i) => (
                <Card key={i} className="border-zinc-800 bg-zinc-900">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-white">{customer.name}</p>
                        <div className="flex items-center gap-3 text-xs text-zinc-400 mt-1">
                          <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{customer.phone}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-emerald-500">${customer.total_spent}</p>
                        <p className="text-xs text-zinc-500">{customer.total_bookings} visits</p>
                        {customer.no_shows > 0 && (
                          <span className="text-xs text-red-400">{customer.no_shows} no-show{customer.no_shows > 1 ? 's' : ''}</span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === "notifications" && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold">Notifications</h2>
            
            {/* Send SMS Card */}
            <Card className="border-zinc-800 bg-zinc-900">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" /> Send SMS
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Input
                  placeholder="Phone number"
                  value={smsPhone}
                  onChange={(e) => setSmsPhone(e.target.value)}
                  className="bg-zinc-800 border-zinc-700"
                />
                <Textarea
                  placeholder="Message..."
                  value={smsMessage}
                  onChange={(e) => setSmsMessage(e.target.value)}
                  className="bg-zinc-800 border-zinc-700"
                  rows={3}
                />
                <Button onClick={handleSendSms} disabled={sendingSms} className="w-full bg-white text-zinc-950">
                  <Send className="h-4 w-4 mr-2" />{sendingSms ? "Sending..." : "Send"}
                </Button>
              </CardContent>
            </Card>

            {/* Notification History */}
            <Card className="border-zinc-800 bg-zinc-900">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Recent Activity</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 max-h-[400px] overflow-y-auto">
                {notifications.slice(0, 20).map((notif) => (
                  <div key={notif.id} className="flex items-center justify-between p-2 bg-zinc-800/50 rounded-lg">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className={cn(
                        "px-2 py-0.5 rounded text-xs flex-shrink-0",
                        notif.notification_type.includes("email") ? "bg-blue-500/20 text-blue-400" : "bg-purple-500/20 text-purple-400"
                      )}>
                        {notif.notification_type.includes("email") ? "Email" : "SMS"}
                      </span>
                      <span className="text-zinc-400 text-xs truncate">{notif.recipient}</span>
                    </div>
                    <span className={cn(
                      "px-2 py-0.5 rounded text-xs flex-shrink-0",
                      notif.status === "sent" ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"
                    )}>
                      {notif.status === "sent" ? "Sent" : "Failed"}
                    </span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-zinc-900/95 backdrop-blur-sm border-t border-zinc-800 z-50 safe-area-bottom">
        <div className="flex justify-around items-center py-2 px-2 max-w-lg mx-auto">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "flex flex-col items-center gap-1 py-2 px-3 rounded-xl transition-all min-w-[60px]",
                activeTab === tab.id 
                  ? "text-white bg-zinc-800" 
                  : "text-zinc-500"
              )}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="h-5 w-5" />
              <span className="text-[10px] font-medium">{tab.label}</span>
            </button>
          ))}
        </div>
      </nav>

      {/* Booking Notes Dialog */}
      <Dialog open={!!selectedBooking} onOpenChange={() => setSelectedBooking(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 mx-4 max-w-sm">
          <DialogHeader>
            <DialogTitle>Booking Notes</DialogTitle>
            <DialogDescription className="text-zinc-400">
              {selectedBooking?.customer_name} - {selectedBooking?.service_name}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Add notes..."
            value={bookingNotes}
            onChange={(e) => setBookingNotes(e.target.value)}
            className="bg-zinc-800 border-zinc-700"
            rows={4}
          />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setSelectedBooking(null)} className="border-zinc-700">Cancel</Button>
            <Button onClick={() => handleSaveNotes(selectedBooking?.id)} className="bg-white text-zinc-950">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Service Modal */}
      <Dialog open={showServiceModal} onOpenChange={setShowServiceModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 mx-4 max-w-sm">
          <DialogHeader>
            <DialogTitle>{editingService ? "Edit Service" : "Add Service"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input value={serviceForm.name} onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })} className="bg-zinc-800 border-zinc-700" placeholder="Service name" />
            <div className="grid grid-cols-2 gap-3">
              <Input type="number" value={serviceForm.price} onChange={(e) => setServiceForm({ ...serviceForm, price: e.target.value })} className="bg-zinc-800 border-zinc-700" placeholder="Price $" />
              <Input type="number" value={serviceForm.duration} onChange={(e) => setServiceForm({ ...serviceForm, duration: e.target.value })} className="bg-zinc-800 border-zinc-700" placeholder="Duration min" />
            </div>
            <Textarea value={serviceForm.description} onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })} className="bg-zinc-800 border-zinc-700" placeholder="Description" />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowServiceModal(false)} className="border-zinc-700">Cancel</Button>
            <Button onClick={handleSaveService} className="bg-white text-zinc-950">{editingService ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Date Override Modal */}
      <Dialog open={showOverrideModal} onOpenChange={setShowOverrideModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 mx-4 max-w-sm">
          <DialogHeader>
            <DialogTitle>Add Date Override</DialogTitle>
            <DialogDescription className="text-zinc-400">Set special hours or a day off</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Calendar
              mode="single"
              selected={overrideDate}
              onSelect={setOverrideDate}
              disabled={(date) => date < new Date()}
              className="rounded-md border border-zinc-800 mx-auto"
            />
            
            <div className="flex gap-4 justify-center">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="overrideType" value="custom" checked={overrideType === "custom"} onChange={(e) => setOverrideType(e.target.value)} />
                <span className="text-sm">Custom Hours</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="overrideType" value="off" checked={overrideType === "off"} onChange={(e) => setOverrideType(e.target.value)} />
                <span className="text-sm">Day Off</span>
              </label>
            </div>

            {overrideType === "custom" && (
              <div className="grid grid-cols-2 gap-3">
                <Select value={overrideStartTime} onValueChange={setOverrideStartTime}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 max-h-[200px]">
                    {TIME_OPTIONS.map((t) => (<SelectItem key={t} value={t}>{formatTimeDisplay(t)}</SelectItem>))}
                  </SelectContent>
                </Select>
                <Select value={overrideEndTime} onValueChange={setOverrideEndTime}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 max-h-[200px]">
                    {TIME_OPTIONS.map((t) => (<SelectItem key={t} value={t}>{formatTimeDisplay(t)}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setShowOverrideModal(false); setOverrideDate(null); }} className="border-zinc-700">Cancel</Button>
            <Button onClick={handleAddOverride} disabled={!overrideDate} className="bg-white text-zinc-950">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
